import { type ReactNode } from 'react';
import clsx from 'clsx';
import { Heading } from '../main';
import styles from './Tile.module.css';

type BaseProps = {
  testid?: string;
  heading: string;
  lead?: ReactNode;
  supportingCopy?: ReactNode;
  badge?: ReactNode;
  slideIn?: boolean;
  selected?: boolean;
  isFocusVisible?: boolean;
  isPressed?: boolean;
  disabled?: boolean; // Controls the visual treatment for disabled state. Caller should apply disabled attribute to interactive element as well.
  className?: string;
};

type ImageProps =
  | {
      imgSrc?: string;
      icon?: never;
      iconSize?: never;
    }
  | {
      imgSrc?: never;
      icon?: ReactNode;
      iconSize: 'small' | 'medium' | 'large';
    };

type AlignmentProps =
  | {
      alignment: 'left';
      likeDislike?: never;
    }
  | {
      alignment: 'center';
      likeDislike?: ReactNode;
    }
  | {
      alignment: 'center-left-aligned-content';
      likeDislike?: ReactNode;
    };

type TileProps = BaseProps & ImageProps & AlignmentProps;

export function Tile(props: TileProps) {
  const {
    testid,
    alignment,
    heading,
    lead,
    supportingCopy,
    badge,
    imgSrc,
    icon,
    iconSize,
    likeDislike,
    slideIn,
    selected,
    isFocusVisible,
    isPressed,
    disabled,
    className,
  } = props;

  const isImageTitle = Boolean(imgSrc);
  const isHeadlineOnly = !supportingCopy && !imgSrc && !icon;

  return (
    <div
      data-testid={testid}
      className={clsx(
        styles.container,
        styles[`container-${alignment}`],
        className,
        {
          [styles['slide-in']]: slideIn,
          [styles['container-headline']]: isHeadlineOnly,
          [styles['container-image']]: isImageTitle,
          [styles.selected]: selected,
          [styles['focus-visible']]: isFocusVisible,
          [styles.pressed]: isPressed,
          [styles.disabled]: disabled,
        }
      )}
    >
      {(icon || imgSrc) && (
        <div
          className={clsx(styles['image-container'], {
            [styles['icon-small']]: iconSize === 'small',
            [styles['icon-medium']]: iconSize === 'medium',
            [styles['icon-large']]: iconSize === 'large',
          })}
        >
          {icon}
          {imgSrc && (
            <img
              data-testid={`${testid}-image`}
              src={imgSrc}
              alt=""
              className={styles.image}
            />
          )}
        </div>
      )}

      <div className={styles.body}>
        <div className={styles['heading-wrapper']}>
          {lead}
          <Heading variant="H6">{heading}</Heading>
          {supportingCopy && (
            <div className={styles.supporting}>{supportingCopy}</div>
          )}
        </div>
        {badge}
      </div>

      {likeDislike && <div className={styles.action}>{likeDislike}</div>}
    </div>
  );
}
